/**
 * @name MenuManagement.jsx
 * @description 메뉴 관리 페이지 (슈퍼관리자 전용)
 *
 * - 좌측: 메뉴 트리 (계층 구조, 클릭으로 선택)
 * - 우측: 선택 메뉴 상세 / 수정 폼 + 신규 메뉴 추가 폼
 */
import React, { useState } from 'react';
import useMenuManagement from '@hooks/comm/useMenuManagement';
import { formatUseYn } from '@utils/formatUseYn';

/** 메뉴 트리 단일 노드 렌더링 */
const MenuTreeNode = ({ menu, depth, selectedCode, onSelect, isFirst, isLast, onSortSwap, pendingSortMap, originalRank, newRank }) => {
    const [expanded, setExpanded] = useState(depth === 0);
    const [hovered, setHovered] = useState(false);
    const hasChildren = menu.children?.length > 0;
    const isSelected = selectedCode === menu.menuCode;
    const isPendingMove = menu.menuCode in pendingSortMap;
    const rankDelta = isPendingMove ? originalRank - newRank : 0; // 양수 = 위로 이동

    const toggle = (e) => {
        e.stopPropagation();
        setExpanded(v => !v);
    };

    const originalChildren = hasChildren
        ? [...menu.children].sort((a, b) => a.sort - b.sort)
        : [];

    const sortedChildren = hasChildren
        ? [...menu.children].sort((a, b) =>
            (pendingSortMap[a.menuCode] ?? a.sort) - (pendingSortMap[b.menuCode] ?? b.sort)
          )
        : [];

    return (
        <div
            style={isPendingMove ? { borderLeft: '3px solid #f97316', borderRadius: '4px', backgroundColor: '#fff7ed' } : { borderLeft: '3px solid transparent' }}
        >
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ display: 'flex', alignItems: 'center', paddingLeft: `${depth * 16}px` }}
            >
                {/* 토글 버튼 영역 — 자식 있는 항목만 표시 */}
                <button
                    onClick={toggle}
                    style={{
                        width: '28px',
                        height: '34px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'none',
                        border: 'none',
                        cursor: hasChildren ? 'pointer' : 'default',
                        color: '#9ca3af',
                        fontSize: '17px',
                        padding: 0,
                    }}
                >
                    {hasChildren ? (expanded ? '▾' : '▸') : ''}
                </button>

                {/* 메뉴명 클릭 → 상세 선택 */}
                <div
                    onClick={() => onSelect(menu)}
                    style={{
                        flex: 1,
                        paddingTop: '7px',
                        paddingBottom: '7px',
                        paddingRight: '8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: depth === 0 ? '22px' : depth === 1 ? '20px' : '18px',
                        backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                        color: isSelected
                            ? '#1d4ed8'
                            : menu.useYn === 'N'
                                ? '#9ca3af'
                                : depth === 0 ? '#111827' : depth === 1 ? '#374151' : '#6b7280',
                        fontWeight: depth === 0
                            ? (isSelected ? '700' : '700')
                            : (isSelected ? '600' : '400'),
                        textDecoration: menu.useYn === 'N' ? 'line-through' : 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        letterSpacing: depth === 0 ? '-0.01em' : 'normal',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'transparent'; }}
                >
                    {menu.menuName}
                </div>

                {/* 이동 배지 — 변경된 항목에만 표시 */}
                {isPendingMove && (
                    <span style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: rankDelta > 0 ? '#ea580c' : '#7c3aed',
                        background: rankDelta > 0 ? '#fff7ed' : '#f5f3ff',
                        border: `1px solid ${rankDelta > 0 ? '#fed7aa' : '#ddd6fe'}`,
                        borderRadius: '10px',
                        padding: '1px 6px',
                        marginRight: '4px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                    }}>
                        {rankDelta > 0 ? `▲ ${rankDelta}` : `▼ ${Math.abs(rankDelta)}`}
                    </span>
                )}

                {/* ↑↓ 버튼 — hover 시만 표시 */}
                <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 150ms', display: 'flex', gap: '2px', flexShrink: 0 }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); onSortSwap(menu, 'up'); }}
                        disabled={isFirst}
                        style={{ padding: '1px 4px', fontSize: '14px', background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? '#d1d5db' : '#6b7280' }}
                        title="위로"
                    >▲</button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onSortSwap(menu, 'down'); }}
                        disabled={isLast}
                        style={{ padding: '1px 4px', fontSize: '14px', background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? '#d1d5db' : '#6b7280' }}
                        title="아래로"
                    >▼</button>
                </div>
            </div>

            {/* 하위 메뉴 — expanded 시에만 렌더 */}
            {hasChildren && expanded && (
                <div>
                    {sortedChildren.map((child, index) => (
                        <MenuTreeNode
                            key={child.menuCode}
                            menu={child}
                            depth={depth + 1}
                            selectedCode={selectedCode}
                            onSelect={onSelect}
                            onSortSwap={onSortSwap}
                            isFirst={index === 0}
                            isLast={index === sortedChildren.length - 1}
                            pendingSortMap={pendingSortMap}
                            originalRank={originalChildren.findIndex(c => c.menuCode === child.menuCode) + 1}
                            newRank={index + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/** 메뉴 폼 (추가 / 수정 공용) */
const MenuForm = ({ initialData, mode, parentMenuCode, defaultSort, allMenusFlat, onSubmit, onCancel, isSubmitting }) => {
    const [form, setForm] = useState({
        menuCode:       initialData?.menuCode       ?? '',
        parentMenuCode: initialData?.parentMenuCode ?? parentMenuCode ?? '',
        menuName:       initialData?.menuName       ?? '',
        icon:           initialData?.icon           ?? '',
        path:           initialData?.path           ?? '',
        sort:           initialData?.sort           ?? defaultSort ?? 1,
        useYn:          initialData?.useYn          ?? 'Y',
    });

    const set = (field) => (e) =>
        setForm(prev => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({ ...form, sort: Number(form.sort) });
    };

    const isEdit = mode === 'edit';

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">메뉴 코드 *</label>
                    <input
                        type="text"
                        value={form.menuCode}
                        onChange={(e) => setForm(prev => ({ ...prev, menuCode: e.target.value.toUpperCase() }))}
                        placeholder="예: COMM_NEW"
                        required
                        disabled={isEdit}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-500 text-base"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">상위 메뉴</label>
                    <select
                        value={form.parentMenuCode}
                        onChange={set('parentMenuCode')}
                        disabled={isEdit}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100"
                    >
                        <option value="">없음 (최상위 메뉴)</option>
                        {allMenusFlat.map(m => (
                            <option key={m.menuCode} value={m.menuCode}>
                                {m.menuName} ({m.menuCode})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">메뉴명 *</label>
                <input
                    type="text"
                    value={form.menuName}
                    onChange={set('menuName')}
                    placeholder="예: 공통코드 관리"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">경로 (Path)</label>
                    <input
                        type="text"
                        value={form.path}
                        onChange={set('path')}
                        placeholder="예: /comm/code"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">아이콘</label>
                    <input
                        type="text"
                        value={form.icon}
                        onChange={set('icon')}
                        placeholder="예: LayoutDashboard"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">정렬 순서</label>
                    <input
                        type="number"
                        value={form.sort}
                        onChange={set('sort')}
                        min={isEdit ? 1 : defaultSort}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">사용 여부</label>
                    <select
                        value={form.useYn}
                        onChange={set('useYn')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        <option value="Y">사용</option>
                        <option value="N">미사용</option>
                    </select>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2.5 text-base text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        취소
                    </button>
                )}
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2.5 text-base font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                    {isSubmitting ? '저장 중...' : (isEdit ? '수정 저장' : '메뉴 등록')}
                </button>
            </div>
        </form>
    );
};

/** flat 트리 추출 (드롭다운용) */
const flattenTree = (nodes, result = []) => {
    nodes.forEach(n => {
        result.push(n);
        if (n.children?.length) flattenTree(n.children, result);
    });
    return result;
};

/** 형제 메뉴 중 최대 sort + 1 계산 (신규 추가 기본값용) */
const calcNextSort = (allMenusFlat, parentMenuCode) => {
    const siblings = allMenusFlat.filter(m =>
        parentMenuCode ? m.parentMenuCode === parentMenuCode : !m.parentMenuCode
    );
    if (siblings.length === 0) return 1;
    return Math.max(...siblings.map(m => m.sort ?? 0)) + 1;
};

const MenuManagement = () => {
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'add'
    const [addParentCode, setAddParentCode] = useState('');
    const [addDefaultSort, setAddDefaultSort] = useState(1);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const {
        menuTree,
        isLoading,
        createMenu,
        updateMenu,
        deleteMenu,
        reorderMenus,
        isCreating,
        isUpdating,
        isDeleting,
        isReordering,
    } = useMenuManagement();

    const [pendingSortMap, setPendingSortMap] = useState({});
    const hasPendingChanges = Object.keys(pendingSortMap).length > 0;

    const allMenusFlat = flattenTree(menuTree).map(m => ({
        ...m,
        sort: pendingSortMap[m.menuCode] ?? m.sort,
    }));

    const originalRoots = [...menuTree].sort((a, b) => a.sort - b.sort);
    const sortedRoots = [...menuTree].sort((a, b) =>
        (pendingSortMap[a.menuCode] ?? a.sort) - (pendingSortMap[b.menuCode] ?? b.sort)
    );

    const handleSelect = (menu) => {
        setSelectedMenu(menu);
        setMode('view');
    };

    const handleAddRoot = () => {
        setSelectedMenu(null);
        setAddParentCode('');
        setAddDefaultSort(calcNextSort(allMenusFlat, null));
        setMode('add');
    };

    const handleAddChild = (parentCode) => {
        setAddParentCode(parentCode);
        setAddDefaultSort(calcNextSort(allMenusFlat, parentCode));
        setMode('add');
    };

    const handleCreate = (dto) => {
        createMenu(dto);
        setMode('view');
        setSelectedMenu(null);
    };

    const handleUpdate = (dto) => {
        updateMenu(selectedMenu.menuCode, dto);
        setMode('view');
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        deleteMenu(deleteTarget);
        setDeleteTarget(null);
        setSelectedMenu(null);
        setMode('view');
    };

    const handleSortSwap = (menu, direction) => {
        const currentSort = pendingSortMap[menu.menuCode] ?? menu.sort;
        const siblings = allMenusFlat
            .filter(m => menu.parentMenuCode
                ? m.parentMenuCode === menu.parentMenuCode
                : !m.parentMenuCode)
            .sort((a, b) => a.sort - b.sort);
        const idx = siblings.findIndex(m => m.menuCode === menu.menuCode);
        const target = direction === 'up' ? siblings[idx - 1] : siblings[idx + 1];
        if (!target) return;
        setPendingSortMap(prev => ({
            ...prev,
            [menu.menuCode]: target.sort,
            [target.menuCode]: currentSort,
        }));
    };

    const handleSaveOrder = () => {
        const payload = Object.entries(pendingSortMap).map(([menuCode, sort]) => ({ menuCode, sort }));
        reorderMenus(payload, { onSuccess: () => setPendingSortMap({}) });
    };

    const handleCancelOrder = () => setPendingSortMap({});

    // 선택된 메뉴의 자식 수 (삭제 가능 여부)
    const selectedHasChildren = selectedMenu?.children?.length > 0;

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">메뉴 관리</h2>

            <div className="flex gap-6">
                {/* ── 좌측: 메뉴 트리 ─────────────────────────────────── */}
                <div className="w-72 flex-shrink-0">
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <span className="text-base font-semibold text-gray-600">메뉴 트리</span>
                            <div className="flex gap-1">
                                {hasPendingChanges ? (
                                    <>
                                        <button
                                            onClick={handleCancelOrder}
                                            className="text-sm px-3 py-1.5 text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            취소
                                        </button>
                                        <button
                                            onClick={handleSaveOrder}
                                            disabled={isReordering}
                                            className="text-sm px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                        >
                                            {isReordering ? '저장 중...' : '순서 저장'}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={handleAddRoot}
                                        className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                        title="최상위 메뉴 추가"
                                    >
                                        + 최상위 추가
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="p-2 max-h-[calc(100vh-220px)] overflow-y-auto">
                            {isLoading ? (
                                <div className="text-center text-gray-400 py-8 text-base">로딩 중...</div>
                            ) : menuTree.length === 0 ? (
                                <div className="text-center text-gray-400 py-8 text-base">메뉴가 없습니다.</div>
                            ) : (
                                sortedRoots.map((menu, index) => (
                                    <MenuTreeNode
                                        key={menu.menuCode}
                                        menu={menu}
                                        depth={0}
                                        selectedCode={selectedMenu?.menuCode}
                                        onSelect={handleSelect}
                                        onSortSwap={handleSortSwap}
                                        isFirst={index === 0}
                                        isLast={index === sortedRoots.length - 1}
                                        pendingSortMap={pendingSortMap}
                                        originalRank={originalRoots.findIndex(m => m.menuCode === menu.menuCode) + 1}
                                        newRank={index + 1}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* ── 우측: 상세 / 폼 ──────────────────────────────────── */}
                <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {mode === 'add' ? (
                        /* 메뉴 추가 폼 */
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-lg font-bold text-gray-800">
                                    {addParentCode
                                        ? `하위 메뉴 추가 — ${addParentCode}`
                                        : '최상위 메뉴 추가'}
                                </h3>
                            </div>
                            <MenuForm
                                mode="add"
                                parentMenuCode={addParentCode}
                                defaultSort={addDefaultSort}
                                allMenusFlat={allMenusFlat}
                                onSubmit={handleCreate}
                                onCancel={() => setMode('view')}
                                isSubmitting={isCreating}
                            />
                        </div>
                    ) : selectedMenu && mode === 'edit' ? (
                        /* 메뉴 수정 폼 */
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-lg font-bold text-gray-800">메뉴 수정</h3>
                            </div>
                            <MenuForm
                                mode="edit"
                                initialData={selectedMenu}
                                allMenusFlat={allMenusFlat}
                                onSubmit={handleUpdate}
                                onCancel={() => setMode('view')}
                                isSubmitting={isUpdating}
                            />
                        </div>
                    ) : selectedMenu ? (
                        /* 메뉴 상세 보기 */
                        <div className="p-6">
                            {/* 헤더 */}
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-bold text-gray-800">{selectedMenu.menuName}</h3>
                                        <span className={`text-sm px-2 py-0.5 rounded-full font-medium
                                            ${selectedMenu.useYn === 'Y'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500 line-through'}`}>
                                            {formatUseYn(selectedMenu.useYn)}
                                        </span>
                                    </div>
                                    <p className="text-base text-gray-400 mt-0.5 font-mono">{selectedMenu.menuCode}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleAddChild(selectedMenu.menuCode)}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                                    >
                                        + 하위 추가
                                    </button>
                                    <button
                                        onClick={() => setMode('edit')}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                                    >
                                        수정
                                    </button>
                                    <button
                                        onClick={() => setDeleteTarget(selectedMenu.menuCode)}
                                        disabled={selectedHasChildren}
                                        title={selectedHasChildren ? '하위 메뉴가 있어 삭제 불가' : '삭제'}
                                        className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        삭제
                                    </button>
                                </div>
                            </div>

                            {/* 상세 정보 카드 */}
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { label: '메뉴 코드',  value: selectedMenu.menuCode,                              mono: true  },
                                    { label: '상위 메뉴',  value: selectedMenu.parentMenuCode || '없음 (최상위)'               },
                                    { label: '경로',       value: selectedMenu.path  || '—',                          mono: true  },
                                    { label: '아이콘',     value: selectedMenu.icon  || '—'                                      },
                                    { label: '정렬 순서',  value: selectedMenu.sort                                              },
                                    { label: '하위 메뉴',  value: `${selectedMenu.children?.length ?? 0}개`                      },
                                ].map(({ label, value, mono }) => (
                                    <div key={label} className="bg-gray-50 rounded-lg px-4 py-3">
                                        <div className="text-sm text-gray-400 mb-1">{label}</div>
                                        <div className={`text-base font-medium text-gray-800 ${mono ? 'font-mono' : ''}`}>
                                            {value}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedHasChildren && (
                                <p className="mt-4 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    하위 메뉴가 있는 메뉴는 삭제할 수 없습니다. 먼저 하위 메뉴를 삭제해주세요.
                                </p>
                            )}

                            {/* 하위 메뉴 미리보기 */}
                            {selectedMenu.children?.length > 0 && (
                                <div className="mt-5">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">하위 메뉴</div>
                                    <div className="space-y-1">
                                        {selectedMenu.children.map(child => (
                                            <div
                                                key={child.menuCode}
                                                className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors text-base"
                                                onClick={() => handleSelect(child)}
                                            >
                                                <span className="flex-1">{child.menuName}</span>
                                                <span className="text-sm text-gray-400 font-mono">{child.menuCode}</span>
                                                <span className="text-xs text-gray-300">{child.path}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* 빈 상태 */
                        <div className="flex items-center justify-center h-64 text-gray-400 text-base">
                            좌측 트리에서 메뉴를 선택하거나 새 메뉴를 추가하세요.
                        </div>
                    )}
                </div>
            </div>

            {/* ── 메뉴 삭제 확인 다이얼로그 ── */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">메뉴 삭제</h3>
                        <p className="text-base text-gray-600 mb-5">
                            <span className="font-semibold text-red-600">{deleteTarget}</span> 메뉴를 삭제합니다.
                            <br />
                            역할에 매핑된 메뉴 정보도 함께 삭제됩니다.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="px-4 py-2.5 text-base text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="px-4 py-2.5 text-base font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {isDeleting ? '삭제 중...' : '삭제'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuManagement;
